import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Icon from './Icon';

const links = [
  ['/', 'Overview', 'grid'],
  ['/summary', 'Summary', 'requests'],
  ['/allocations', 'Allocations', 'calendar'],
  ['/professionals', 'Professionals', 'people'],
  ['/chargeability', 'Chargeability', 'chart'],
  ['/requests', 'Requests', 'requests'],
  ['/recruitment', 'Recruitment', 'recruitment']
];
const adminLinks = [
  ['/clients', 'Clients'],
  ['/engagements', 'Engagements'],
  ['/hours-report', 'Hours report'],
  ['/file-upload', 'Import data'],
  ['/professional-load', 'Professional import'],
  ['/admin', 'User management']
];

export default function AppHeader({ name, isAdmin, logout }) {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const header = useRef(null);
  const location = useLocation();
  useEffect(() => {
    setOpen(null);
    setMobile(false);
  }, [location.pathname]);
  useEffect(() => {
    const close = e => {
      if (!header.current?.contains(e.target)) {
        setOpen(null);
        setMobile(false);
      }
    };
    const escape = e => {
      if (e.key === 'Escape') {
        header.current?.querySelector('[aria-expanded="true"]')?.focus();
        setOpen(null);
        setMobile(false);
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, []);
  const initials = (name || 'User')
    .trim()
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean);
  return (
    <header className='workspace-header' ref={header}>
      <Link to='/' className='workspace-brand' aria-label='Scheduling home'>
        <span className='brand-symbol'>
          <Icon name='calendar' size={21} />
        </span>
        <span>
          Scheduling<span className='brand-dot'>.</span>
        </span>
      </Link>
      <button className='workspace-menu-button' aria-label='Toggle navigation' aria-expanded={mobile} aria-controls='workspace-navigation' onClick={() => setMobile(!mobile)}>
        <Icon name={mobile ? 'close' : 'menu'} />
      </button>
      <nav id='workspace-navigation' aria-label='Main navigation' className={`workspace-nav ${mobile ? 'is-open' : ''}`}>
        {links.map(([to, label, icon]) => (
          <NavLink key={to} to={to} end={to === '/'}>
            <Icon name={icon} size={16} />
            <span>{label}</span>
          </NavLink>
        ))}
        {isAdmin && (
          <div className='workspace-dropdown'>
            <button
              className={`workspace-nav-trigger ${adminLinks.some(([p]) => location.pathname.startsWith(p)) ? 'active' : ''}`}
              aria-expanded={open === 'admin'}
              aria-controls='workspace-admin'
              onClick={() => setOpen(open === 'admin' ? null : 'admin')}>
              <Icon name='settings' size={16} />
              Manage
              <Icon name='chevron' size={13} />
            </button>
            {open === 'admin' && (
              <div className='workspace-dropdown-panel' id='workspace-admin'>
                <span className='dropdown-caption'>Workspace management</span>
                {adminLinks.map(([to, label]) => (
                  <NavLink to={to} key={to}>
                    {label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>
      <div className='workspace-dropdown workspace-account'>
        <button
          className='workspace-account-trigger'
          aria-label='Account menu'
          aria-expanded={open === 'account'}
          aria-controls='workspace-account'
          onClick={() => setOpen(open === 'account' ? null : 'account')}>
          <span className='account-avatar'>
            {initials[0]}
            {initials.length > 1 ? initials[initials.length - 1] : ''}
          </span>
          <span className='account-name'>{name || 'My account'}</span>
          <Icon name='chevron' size={13} />
        </button>
        {open === 'account' && (
          <div className='workspace-dropdown-panel account-panel' id='workspace-account'>
            <span className='dropdown-caption'>{isAdmin ? 'Administrator' : 'Team member'}</span>
            <strong>{name}</strong>
            <button onClick={logout}>
              <Icon name='logout' size={16} />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
